import React, { useState, useRef, useEffect, useCallback } from 'react';
import { IoClose } from 'react-icons/io5';
import clsx from 'clsx';

import useMountTransition from 'hooks/useMountTransition';
import { downloadImage } from 'utils/images';
import { useImgLightboxStyles } from './utils/useImgLightboxStyles';

import './lightbox.scss';

const LIGHTBOX_VERTICAL_PADDING = 20;
const LIGHTBOX_HORIZONTAL_PADDING = 40;

const Lightbox = (props) => {
  const { src, filename, ...rest } = props;

  const [isOpen, setIsOpen] = useState(false);

  const isTransitioning = useMountTransition(isOpen, 200);

  const dismiss = () => {
    setIsOpen(false);
  };

  const download = () => {
    downloadImage(src, filename);
  };

  const imgRef = useRef();

  // Scroll lock
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => document.body.style.removeProperty('overflow');
  }, [isOpen]);

  const {
    buttonTranslateX,
    aspectRatio,
    imageWidth,
    scale,
    translateX,
    translateY,
    height,
    width,
  } = useCallback(
    useImgLightboxStyles({ isOpen, container: window, img: imgRef }),
    [imgRef, isOpen, isTransitioning],
  );

  // Horizontal aspect image calculations are still a little off, so need to position button differently.
  const buttonStyle = {
    ... width ? {
      left: `${width - 110}px`, // Position the button left of the width of transformed image minus button width.
      transform: `translateX(${buttonTranslateX})`,
    } : {
      right: 0,
      transform: `translateX(${buttonTranslateX})`,
    },
  };

  return (
    <>
      {(isOpen || isTransitioning) && (
        <div
          onClick={dismiss}
          className={clsx('ap-lightbox', isTransitioning && 'in', isOpen && 'open')}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="ap-lightbox__inner"
            style={{
              ... height && { height: height + (LIGHTBOX_VERTICAL_PADDING * 2 + 30) },
              ... width && { width: width + (LIGHTBOX_HORIZONTAL_PADDING * 2 + 30) },
            }}
          >
            <button
              className="ap-lightbox__close button--unstyled"
              onClick={dismiss}
            >
              <IoClose
                className="ap-lightbox__close-icon"
                size={20}
              />
            </button>
          </div>
        </div>
      )}
      <div
        className="ap-lightbox-image"
        style={{
          zIndex: isOpen ? 1001 : 'initial',
          position: isOpen ? 'relative' : 'static',
          transform: `translate(${translateX}px, ${translateY}px)`,
          ... height && { height },
          ... isOpen && {
            aspectRatio,
          },
        }}
      >
        <img
          ref={imgRef}
          {...rest}
          src={src}
          onClick={() => setIsOpen(true)}
          style={{
            width: imageWidth,
            ... scale > 1 && {
              transformOrigin: '0 0',
              transform: `scale(${scale})`,
            },
          }}
        />
        {isOpen && (
          <button
            className="ap-lightbox-image__download button--unstyled button--menu download-link"
            onClick={download}
            style={buttonStyle}
          >
            Download
          </button>
        )}
      </div>
    </>
  );
};

export default Lightbox;
